<template>
    <Card>
        <div class="d-flex align-items-center">
            <h4 class="card-subtitle flex-fill m-0">Prior Authorizations</h4>
            <b-button v-if="value && value.id && canCreatePA" id="pa-create-button" variant="info" class="m-0"
                @click="newPriorAuthorization">
                Create PA
            </b-button>
        </div>
        <b-row>
            <b-col>
                <checkbox v-model="showClosed">
                    Include Closed PAs
                    <HelpPopup>Closed PAs include abandoned, approved, denied and cancelled authorizations.</HelpPopup>
                </checkbox>
                <b-table id="prior-authorization-table" striped responsive="true" sort-icon-left
                    :items="filteredPriorAuthorizations" :fields="fields" @row-clicked="selectPa"
                    class="cursor-pointer">
                    <template #cell(created)="data">
                        {{ data.value }}
                        <div>
                            <small>{{ data.item.createdBy }}</small>
                        </div>
                    </template>
                    <template #cell(updated)="data">
                        {{ data.value }}
                        <div>
                            <small>{{ data.item.updatedBy }}</small>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-modal title="Create Prior Authorization" id="prior-authorization-create-modal" @hide="createModalHide"
            size="xl" v-if="value && value.id" hide-footer>
            <PriorAuthorizationCreate v-model="selectedPrescription" :patientId="value.id" />
        </b-modal>

        <PriorAuthorizationDetails id="pa-details-modal" :value="selectedPa" />
    </Card>

</template>

<script lang="ts">
import { Component, Watch, Vue, Prop } from 'vue-property-decorator';
import Card from "@/components/Cards/Card.vue";
import Axios from 'axios';
import moment from 'moment';
import PriorAuthorizationCreate from "@/components/PriorAuthorization/PriorAuthorizationCreate.vue";
import PriorAuthorizationDetails from "@/components/PriorAuthorization/PriorAuthorizationDetails.vue";
import { Prescription } from '@/models/Prescription';
import { PriorAuthorization, PriorAuthorizationStatusCode, PriorAuthorizationType } from '@/models/PriorAuthorization';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import HelpPopup from '../HelpPopup.vue';
import { Permission } from '@/models/Permission';

@Component({
    name: "PriorAuthorizationList",
    components: {
        Card,
        PriorAuthorizationCreate,
        PriorAuthorizationDetails,
        Checkbox,
        HelpPopup
    }
})
export default class PriorAuthorizationListComponent extends Vue {
    @Prop() private value: any;

    private priorAuthorizations: PriorAuthorization[] = [];
    private selectedPa: PriorAuthorization = {} as PriorAuthorization;
    private selectedPrescription: Prescription = {} as Prescription;
    private showClosed = false;

    created() {
        this.fetchPriorAuthorizations();
    }

    @Watch("value")
    valueUpdated() {
        this.priorAuthorizations = [];
        this.fetchPriorAuthorizations();
    }

    private fields: any[] = [
        { key: "id" },
        { key: "prescription", formatter: (p: Prescription) => p ? `${p.storeID}-${p.rxNumber}-${p.rfNumber}` : 'Non-Script' },
        { key: "type", formatter: (d: PriorAuthorizationType) => PriorAuthorizationType[d] },
        { key: "status", formatter: (s: PriorAuthorizationStatusCode) => PriorAuthorizationStatusCode[s], sortable: true },
        { key: "updated", formatter: (d: any) => moment.utc(d).format("L"), sortable: true },
        { key: "created", formatter: (d: any) => moment.utc(d).format("L"), sortable: true },
    ];

    createModalHide() {
        this.fetchPriorAuthorizations();
    }

    fetchPriorAuthorizations() {
        let url = `/PriorAuthorization/`;
        if (this.value?.id) url = `/PriorAuthorization/ByPatient/${this.value.id}`;
        Axios.get<Array<PriorAuthorization>>(url)
            .then(response => {
                if (response.data?.length) this.priorAuthorizations = response.data.map(pa => new PriorAuthorization(pa));
            })
            .catch(error => {
                console.error('Error loading PAs', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    newPriorAuthorization() {
        this.$bvModal.show("prior-authorization-create-modal");
    }

    selectPa(item: PriorAuthorization) {
        this.selectedPa = item;
        this.$bvModal.show("pa-details-modal");
    }

    get filteredPriorAuthorizations() {
        if (this.showClosed) return this.priorAuthorizations;
        return this.priorAuthorizations.filter(pa => ![
            PriorAuthorizationStatusCode.Abandoned,
            PriorAuthorizationStatusCode.Approved,
            PriorAuthorizationStatusCode.Cancelled,
            PriorAuthorizationStatusCode.Denied].some(s => s == pa.status));
    }

    get canCreatePA(): boolean {
        return this.$permissions.some((p: Permission) => 
            p.permissionCodeName.toLowerCase() === "services/prior authorization" ||
            p.permissionCodeName.toLowerCase() === "patient dashboard/create pa"
        );
    }
}
</script>
